import { useCallback, useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { AnimatePresence, motion } from 'framer-motion'
import { DURATION, EASE_IN, EASE_OUT } from '../tokens/motion'

const DELAY_MS = 400
const OFFSET = 8
const MARGIN = 8

type Side = 'top' | 'bottom' | 'right'

/** A label for a control that has none. Opens on hover after a pause, or on keyboard focus. */
export function Tooltip({
  label,
  side = 'top',
  onlyWhenClipped = false,
  className,
  children,
}: {
  label: string
  side?: Side
  /** Open only while the text inside is cut off, so a name shown whole never repeats itself. */
  onlyWhenClipped?: boolean
  className?: string
  children: ReactNode
}) {
  const [open, setOpen] = useState(false)
  const [position, setPosition] = useState<{ top: number; left: number } | null>(null)
  const timer = useRef<number | null>(null)
  const anchor = useRef<HTMLSpanElement>(null)
  const bubble = useRef<HTMLSpanElement>(null)

  const place = useCallback(() => {
    const trigger = anchor.current?.getBoundingClientRect()
    const box = bubble.current?.getBoundingClientRect()
    if (!trigger || !box) return
    const top =
      side === 'right'
        ? trigger.top + trigger.height / 2 - box.height / 2
        : side === 'top'
          ? trigger.top - box.height - OFFSET
          : trigger.bottom + OFFSET
    const left = side === 'right' ? trigger.right + OFFSET : trigger.left + trigger.width / 2 - box.width / 2
    setPosition({
      top: Math.min(Math.max(top, MARGIN), window.innerHeight - box.height - MARGIN),
      left: Math.min(Math.max(left, MARGIN), window.innerWidth - box.width - MARGIN),
    })
  }, [side])

  useLayoutEffect(() => {
    if (open) place()
    else setPosition(null)
  }, [open, place])

  const clear = () => {
    if (timer.current !== null) window.clearTimeout(timer.current)
    timer.current = null
  }
  const schedule = () => {
    clear()
    if (onlyWhenClipped) {
      const text = anchor.current?.firstElementChild
      if (!text || text.scrollWidth <= text.clientWidth) return
    }
    timer.current = window.setTimeout(() => setOpen(true), DELAY_MS)
  }
  const dismiss = () => {
    clear()
    setOpen(false)
  }
  useEffect(() => clear, [])

  return (
    <span
      ref={anchor}
      className={className ?? 'relative inline-flex'}
      onPointerEnter={schedule}
      onPointerLeave={dismiss}
      onPointerDown={dismiss}
      onFocus={(event) => {
        if (event.target instanceof HTMLElement && event.target.matches(':focus-visible')) schedule()
      }}
      onBlur={dismiss}
    >
      {children}
      {createPortal(
        <AnimatePresence>
          {open ? (
            <motion.span
              ref={bubble}
              role="tooltip"
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1, transition: { duration: DURATION.fast, ease: EASE_OUT } }}
              exit={{ opacity: 0, scale: 0.98, transition: { duration: DURATION.fast, ease: EASE_IN } }}
              style={{ top: position?.top ?? 0, left: position?.left ?? 0, visibility: position ? 'visible' : 'hidden' }}
              className="pointer-events-none fixed z-[250] w-max max-w-[min(28rem,calc(100vw-1rem))] rounded-lg bg-primary px-2 py-1 text-caption text-primary-foreground shadow-overlay"
            >
              {label}
            </motion.span>
          ) : null}
        </AnimatePresence>,
        document.body,
      )}
    </span>
  )
}

/**
 * One line of text that ends in an ellipsis when it runs out of room, and
 * shows itself whole in a tooltip only then.
 */
export function Truncate({ text, className, side = 'top' }: { text: string; className?: string; side?: Side }) {
  return (
    <Tooltip label={text} side={side} onlyWhenClipped className="relative block min-w-0">
      <span className={['block truncate', className].filter(Boolean).join(' ')}>{text}</span>
    </Tooltip>
  )
}
